import * as React from "react"

import { useToast } from "@/components/ui/toast"

import { QUEUE_FILTER_IDS } from "../queue-filters"

/**
 * Says, as a toast, that a linked `?assignee=` could not be applied because
 * the staff list (`/users`) failed to load. Without the roster the options are
 * "Me" only, so the link's other assignees are dropped and the queue is not
 * narrowed by them; saying nothing made that look like the filter had worked.
 *
 * Only then: a hand-typed assignee that is simply unknown is dropped quietly,
 * as before, because the list it was checked against did load.
 *
 * Once per failure. `rosterFailedAt` is the failure's time, so a retry that
 * fails again says so again, while a re-render (or StrictMode's double effect)
 * of the same failure does not.
 */
export function useAssigneeFilterToast({
  rosterFailedAt,
  droppedFilterIds,
  onRetry,
}: {
  rosterFailedAt: number | null
  droppedFilterIds: readonly string[]
  onRetry: () => void
}): void {
  const { toast } = useToast()
  const assigneeDropped = droppedFilterIds.includes(QUEUE_FILTER_IDS.assignee)
  const announced = React.useRef<number | null>(null)

  React.useEffect(() => {
    if (rosterFailedAt === null || !assigneeDropped) return
    if (announced.current === rosterFailedAt) return
    announced.current = rosterFailedAt
    toast({
      tone: "error",
      title: "The Assignee filter from this link wasn't applied",
      body: "The staff list couldn't be loaded, so the queue isn't narrowed to the assignees the link asked for.",
      action: { label: "Try again", onAction: onRetry },
    })
  }, [rosterFailedAt, assigneeDropped, toast, onRetry])
}
